'use client';

import { useMemo } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { parseAsString, useQueryStates } from 'nuqs';

import { ProductPriceCardItem } from '../models/product-price-card-item.model';
import { createFilterSpecification } from '../utils/create-filter-specification';

import { ProductPriceListClient } from './product-price-list-client';
import { ProductPriceListFilters } from './product-price-list-filters';

interface Props {
   productPriceCardItems: ProductPriceCardItem[];
   productDetailBaseRoute: string;
   quoteDetailBaseRoute: string;
   tdsAnchor: string | undefined;
   channel: string;
   showShoppingCartBtn: boolean;
   showRequestQuoteOption: boolean;
}

export const ProductPriceListWithFilter = ({
   productPriceCardItems,
   productDetailBaseRoute,
   quoteDetailBaseRoute,
   tdsAnchor,
   channel,
   showShoppingCartBtn,
   showRequestQuoteOption
}: Props) => {
   const translate = useTranslations();

   const [filterValue, setFilterValue] = useQueryStates({
      search: parseAsString.withDefault(''),
      brand: parseAsString.withDefault(''),
      polymerType: parseAsString.withDefault(''),
      orderedFromDate: parseAsString.withDefault(''),
      orderedUntilDate: parseAsString.withDefault('')
   });

   const filteredItems = useMemo(() => {
      const filterSpecification = createFilterSpecification(filterValue);
      return productPriceCardItems.filter(filterSpecification.isSatisfiedBy);
   }, [productPriceCardItems, filterValue]);

   return (
      <Box direction="column">
         <ProductPriceListFilters productPriceCardItems={filteredItems} value={filterValue} onChange={setFilterValue} />
         <Typography variant="small">{translate('prices.total-products', { total: filteredItems.length.toString() })}</Typography>
         {filteredItems.length === 0 && (
            <Box direction="column" items="center" gap="none">
               <Typography variant="small" color="subtle">
                  {translate('autocomplete-no-results-title')}
               </Typography>
               <Typography variant="small" color="subtle">
                  {translate('autocomplete-no-results-description')}
               </Typography>
            </Box>
         )}
         <ProductPriceListClient
            productPriceCardItems={filteredItems}
            productDetailBaseRoute={productDetailBaseRoute ?? ''}
            quoteDetailBaseRoute={quoteDetailBaseRoute}
            tdsAnchor={tdsAnchor}
            channel={channel}
            showShoppingCartBtn={showShoppingCartBtn}
            showRequestQuoteOption={showRequestQuoteOption}
         />
      </Box>
   );
};
